import type {
  AnswerPayload,
  CellValue,
  FieldTypeCode,
  IGridColumn
} from '../api/contracts';

/**
 * The two-way translation between survey-api's typed answer payload and what a
 * control holds.
 *
 * Controls work in strings (an `<input>` has no other state) and option-code
 * arrays; the wire wants `{"t":"int","v":3}`. The mapping is per **field type**,
 * not per control, because that is what the server validates a write against:
 * sending `{"t":"text"}` for an `int` usage is a rejected batch, not a coerced value.
 */

/** What a control holds: scalar text, selected option codes, or grid cells. */
export type UiValue = string | string[] | Record<string, string>;

const CURRENCY = 'USD';

/** The payload tag each scalar/choice field type must answer with. */
const TAG_BY_FIELD_TYPE: Record<string, AnswerPayload['t']> = {
  text_short: 'text',
  text_area: 'text',
  email: 'text',
  phone: 'text',
  url: 'text',
  int: 'int',
  year: 'int',
  number: 'num',
  percent: 'pct',
  currency: 'money',
  date: 'date',
  radio: 'opts',
  dropdown: 'opts',
  multi_select: 'opts',
  checkbox: 'opts',
  attestation: 'opts',
  grid: 'cells',
  multi_year_table: 'cells'
};

/** Field types that hold no answer of their own — containers and display copy. */
export const CONTAINER_FIELD_TYPES = new Set<FieldTypeCode>([
  'repeat_group',
  'percent_group',
  'display_note'
]);

/**
 * A field answered by picking, not by typing. The distinction decides *when* an
 * answer is sent: a choice is complete the instant it is made, whereas typed
 * text is only complete when the respondent leaves the field — half a number is
 * not a smaller number, it is a different one.
 */
export function isChoiceField(fieldType: FieldTypeCode): boolean {
  return TAG_BY_FIELD_TYPE[fieldType] === 'opts';
}

export function isCellsField(fieldType: FieldTypeCode): boolean {
  return TAG_BY_FIELD_TYPE[fieldType] === 'cells';
}

/** The empty value a control starts from, so `undefined` never reaches an input. */
function emptyValue(fieldType: FieldTypeCode): UiValue {
  if (isChoiceField(fieldType)) return [];
  if (isCellsField(fieldType)) return {};
  return '';
}

/** Wire → control. */
export function decodeAnswer(payload: Nullable<AnswerPayload>, fieldType: FieldTypeCode): UiValue {
  if (!payload) return emptyValue(fieldType);

  switch (payload.t) {
    case 'opts':
      return [...payload.v];
    case 'cells':
      return Object.fromEntries(
        Object.entries(payload.v).map(([column, cell]) => [column, cellToText(cell)])
      );
    case 'text':
    case 'date':
      return payload.v;
    default:
      return String(payload.v);
  }
}

/**
 * Control → wire. Returns null for an empty value, which is exactly what the
 * write endpoint reads as "clear this answer" — so erasing a field and saving
 * removes the answer rather than storing an empty string the engine would then
 * treat as answered.
 */
export function encodeAnswer(
  value: UiValue,
  fieldType: FieldTypeCode,
  columns: IGridColumn[] = []
): Nullable<AnswerPayload> {
  const tag = TAG_BY_FIELD_TYPE[fieldType];
  if (!tag) return null;
  return encodeAnswerAs(value, tag, columns);
}

/**
 * Control → wire by the payload's tag, for a caller that holds the tag rather than a field type:
 * a list's answer cell (SR-403), whose `editType` survey-api sends as the respondent route's value
 * tag. One encoder, so an answer the grid writes is the payload the form would — the currency
 * included — and the field-type table stays this module's alone.
 */
export function encodeAnswerAs(
  value: UiValue,
  tag: AnswerPayload['t'],
  columns: IGridColumn[] = []
): Nullable<AnswerPayload> {
  if (tag === 'opts') {
    const codes = (value as string[]).filter(Boolean);
    return codes.length > 0 ? { t: 'opts', v: codes } : null;
  }

  if (tag === 'cells') {
    const cells = value as Record<string, string>;
    const encoded: Record<string, CellValue> = {};
    for (const column of columns) {
      const raw = cells[column.code];
      const cell = encodeCell(raw, column.input);
      if (cell !== null) encoded[column.code] = cell;
    }
    return Object.keys(encoded).length > 0 ? { t: 'cells', v: encoded } : null;
  }

  const text = (value as string).trim();
  if (text === '') return null;

  switch (tag) {
    case 'text':
      return { t: 'text', v: text };
    case 'date':
      // The payload is yyyy-MM-dd or nothing; a partial date is not a value.
      return /^\d{4}-\d{2}-\d{2}$/.test(text) ? { t: 'date', v: text } : null;
    case 'int': {
      const parsed = number(text);
      return Number.isInteger(parsed) ? { t: 'int', v: parsed } : null;
    }
    case 'num': {
      const parsed = number(text);
      return Number.isFinite(parsed) ? { t: 'num', v: parsed } : null;
    }
    case 'pct': {
      const parsed = number(text);
      return Number.isFinite(parsed) ? { t: 'pct', v: parsed } : null;
    }
    case 'money': {
      const parsed = number(text);
      return Number.isFinite(parsed) ? { t: 'money', v: parsed, cur: CURRENCY } : null;
    }
    default:
      return null;
  }
}

/**
 * Display text read as a number.
 *
 * The punctuation stripped here is **presentation, not value**: a thousands separator, a
 * currency symbol on an amount, a trailing `%` on a field whose label already says
 * percent. Typing them is how people write these numbers, and treating `1,500,000` or
 * `12.5%` as unparseable would flag a value that is perfectly well formed.
 *
 * One rule, not one per tag: the money branch stripped `[,$\s]` and the other three
 * stripped nothing, so `1,500` was an amount in one field and a malformed count in the
 * next one over. `,` as a thousands separator (not a decimal comma) follows the rest of
 * the app, which formats with `toLocaleString('en-US')`.
 *
 * `Number('')` is 0, so a string that is nothing but punctuation must not reach it —
 * `12.5%` stripping to `12.5` is the point, `%` stripping to `0` is not.
 */
function number(text: string): number {
  const bare = text.replace(/[,$%\s]/g, '');
  return bare === '' ? Number.NaN : Number(bare);
}

/** The same reading for the admin domain's internal fields (`internal-field-values.ts`), whose
 *  numbers are typed into the same controls — one rule for what `1,500` means, not two. */
export { number as readNumber };

/**
 * An amount as an amount's box shows it while it is not focused (the survey review's G16, D11):
 * the digits before the point grouped in threes, `en-US`, what follows the point as typed. Text that
 * is no plain number — `abc`, a half-typed `1.2.3` — is shown as it is, so the box never hides
 * what the respondent has to correct. Display only: `number` above strips the grouping again.
 */
export function groupedAmount(text: string): string {
  const match = /^(-?)(\d+)(\.\d*)?$/.exec(text.replace(/[,$\s]/g, ''));
  if (!match) return text;
  return `${match[1]}${match[2].replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${match[3] ?? ''}`;
}

/**
 * Whether the control holds something **no payload of this field's type can carry** —
 * `abc` in a count, `12.5.6` in a percent, a half-typed date.
 *
 * This is not validation and must not grow into it: whether a value is *acceptable* is
 * the engine's judgement and arrives as a finding. This answers a narrower, purely local
 * question the encoder above already decides every time it runs — "can I build the wire
 * payload at all" — and it exists because of what the answer used to be spent on.
 *
 * `encodeAnswer` returns null for two situations that are not the same thing: an empty
 * control, where null means *clear this answer* and is exactly right, and unparseable
 * text, where it also meant clear. So typing `abc` into a count either erased itself on
 * blur or, worse, sent a write that deleted a number the respondent had saved earlier.
 * Both are the one thing survey-api's own write path refuses to do — it stores the value
 * and flags it, "so the respondent never loses typed input".
 *
 * Blank is decided per shape, and a cells document is decided **per cell**: a row whose
 * text column parses and whose count column does not would otherwise save the half that
 * worked and drop the other in silence, which is the same loss one level down.
 */
export function isMalformed(
  value: UiValue,
  fieldType: FieldTypeCode,
  columns: IGridColumn[] = []
): boolean {
  if (isChoiceField(fieldType)) return false;

  if (isCellsField(fieldType)) {
    const cells = value as Record<string, string>;
    return columns.some(
      (column) =>
        (cells[column.code] ?? '').trim() !== '' &&
        encodeCell(cells[column.code], column.input) === null
    );
  }

  const text = typeof value === 'string' ? value.trim() : '';
  return text !== '' && encodeAnswer(value, fieldType, columns) === null;
}

/**
 * What to tell a respondent whose value cannot be carried. Named by the shape the field
 * wants, never by the tag on the wire: `{"t":"pct"}` means nothing to an advisor.
 */
export function malformedHint(fieldType: FieldTypeCode): string {
  switch (TAG_BY_FIELD_TYPE[fieldType]) {
    case 'int':
      return 'Enter a whole number — digits only.';
    case 'num':
    case 'pct':
      return 'Enter a number, for example 12.5.';
    case 'money':
      return 'Enter an amount, for example 1,500,000.';
    case 'date':
      return 'Enter a full date.';
    case 'cells':
      return 'One of these cells is not a number.';
    default:
      return 'This entry cannot be saved as it is.';
  }
}

/**
 * Whether two payloads say the same thing.
 *
 * Guards the write queue against answers that did not change: tabbing through a
 * pre-filled section, or typing a value back to what it already was, must not
 * cost a request. Every write returns a whole new state document and adopting
 * one re-renders the form, so a no-op write is not free — it is a visible one.
 *
 * Option order is treated as significant. Comparing unordered would need to know
 * which usages rank their options and which merely collect them, and that is the
 * definition's business, not this file's.
 */
export function sameAnswer(a: Nullable<AnswerPayload>, b: Nullable<AnswerPayload>): boolean {
  if (a === null || b === null) return a === b;
  if (a.t !== b.t) return false;

  switch (a.t) {
    case 'opts': {
      const other = (b as Extract<AnswerPayload, { t: 'opts' }>).v;
      return a.v.length === other.length && a.v.every((code, index) => code === other[index]);
    }
    case 'cells': {
      const other = (b as Extract<AnswerPayload, { t: 'cells' }>).v;
      const columns = Object.keys(a.v);
      return (
        columns.length === Object.keys(other).length &&
        columns.every((column) => a.v[column] === other[column])
      );
    }
    case 'money':
      return a.v === b.v && a.cur === (b as Extract<AnswerPayload, { t: 'money' }>).cur;
    default:
      return a.v === (b as { v: unknown }).v;
  }
}

/**
 * A stored row narrowed to the cells a write of it carries — `columns`, the row's writable ones
 * (`rowWriteColumns`) — or null where none of them holds a value; any other payload untouched.
 *
 * What a no-op is judged against (B4-3): the stored row holds the engine's computed cells too, so
 * compared whole it never equals a row encoded from the writable cells, and every blur would send
 * a write — and a computed cell moving under the row is not the respondent's edit.
 */
export function writableCells(
  payload: Nullable<AnswerPayload>,
  columns: IGridColumn[]
): Nullable<AnswerPayload> {
  if (payload?.t !== 'cells') return payload;
  const kept: Record<string, CellValue> = {};
  for (const column of columns) {
    if (column.code in payload.v) kept[column.code] = payload.v[column.code];
  }
  return Object.keys(kept).length > 0 ? { t: 'cells', v: kept } : null;
}

/** A cell is typed by its column's `input`, so encoding is decided there. */
function encodeCell(raw: string | undefined, input: IGridColumn['input']): CellValue {
  if (input === 'checkbox') return raw === 'true' ? true : null;

  const text = (raw ?? '').trim();
  if (text === '') return null;

  switch (input) {
    case 'int': {
      const parsed = number(text);
      return Number.isInteger(parsed) ? parsed : null;
    }
    case 'num':
    case 'currency':
    case 'percent': {
      const parsed = number(text);
      return Number.isFinite(parsed) ? parsed : null;
    }
    default:
      return text;
  }
}

function cellToText(cell: CellValue): string {
  if (cell === null || cell === undefined) return '';
  if (typeof cell === 'boolean') return cell ? 'true' : '';
  return String(cell);
}

/** Read-only display of an answer, for the review panel and submitted records. */
export function formatAnswer(
  payload: Nullable<AnswerPayload>,
  optionLabels?: Map<string, string>
): string {
  if (!payload) return '—';

  switch (payload.t) {
    case 'opts':
      return payload.v.map((code) => optionLabels?.get(code) ?? code).join(', ');
    case 'money':
      return `${payload.cur} ${payload.v.toLocaleString('en-US')}`;
    case 'pct':
      return `${payload.v}%`;
    case 'date': {
      // As the questions ask for a date (mm/dd/yyyy), not as it is stored.
      const [year, month, day] = payload.v.split('-');
      return day ? `${month}/${day}/${year}` : payload.v;
    }
    case 'cells':
      return Object.entries(payload.v)
        .map(([column, cell]) => `${column}: ${cellToText(cell) || '—'}`)
        .join(' · ');
    default:
      return String(payload.v);
  }
}
